import React, { useState, useEffect, useCallback } from 'react';
import { getWeekStart, addDaysYMD } from '../lib/utils';
import { useSearchParams } from 'react-router-dom';
import { driversApi, driverDeductionsApi, driverAddonsApi, driverFilesApi, unitsApi, aiApi } from '../lib/api';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Users, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  Phone,
  Mail,
  Truck,
  AlertCircle,
  UserCheck,
  DollarSign,
  FileText,
  Calendar,
  Download,
  TrendingUp,
  Minus,
  X,
  Gift,
  Upload,
  Sparkles,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { DragDropZone } from '../components/DragDropZone';
import { Checkbox } from '../components/ui/checkbox';
import { DriverPaystubLive } from '../components/DriverPaystubLive';

export default function Drivers() {
  const { canEdit, isAdmin } = useAuth();
  const [drivers, setDrivers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zipcode: '',
    license_number: '',
    license_expiry: '',
    status: 'active',
    assigned_unit_id: 'none',
    unit_start_date: '',
    is_owner_operator: false,
    ssn_last_four: '',
    pay_type: 'percentage',
    pay_rate: '',
    auto_deduct_from_unit: false, // Auto-deduct driver pay from assigned unit
    show_load_amounts_on_paystub: false,
    block_pay: '',
  });

  // Paystub dialog state
  const [paystubDialogOpen, setPaystubDialogOpen] = useState(false);
  const [selectedDriverForPaystub, setSelectedDriverForPaystub] = useState(null);
  const [paystubStartDate, setPaystubStartDate] = useState('');
  const [paystubEndDate, setPaystubEndDate] = useState('');
  const [generatingPaystub, setGeneratingPaystub] = useState(false);

  // Pay summaries for all drivers
  const [paySummaries, setPaySummaries] = useState({});
  const [loadingSummaries, setLoadingSummaries] = useState(false);

  // Deductions management state
  const [deductionsDialogOpen, setDeductionsDialogOpen] = useState(false);
  const [selectedDriverForDeductions, setSelectedDriverForDeductions] = useState(null);
  const [driverDeductions, setDriverDeductions] = useState([]);
  const [loadingDeductions, setLoadingDeductions] = useState(false);
  const [deductionFormData, setDeductionFormData] = useState({
    name: '',
    amount: '',
    deduction_type: 'advance',
    week_start: '',
    notes: ''
  });

  // Addons management state (bonuses, etc.)
  const [addonsDialogOpen, setAddonsDialogOpen] = useState(false);
  const [selectedDriverForAddons, setSelectedDriverForAddons] = useState(null);
  const [driverAddons, setDriverAddons] = useState([]);
  const [loadingAddons, setLoadingAddons] = useState(false);
  const [addonFormData, setAddonFormData] = useState({
    name: '',
    amount: '',
    addon_type: 'bonus',
    week_start: '',
    notes: ''
  });

  // OCR upload state
  const [ocrDialogOpen, setOcrDialogOpen] = useState(false);
  const [extractingInfo, setExtractingInfo] = useState(false);
  const [uploadedApplicationFile, setUploadedApplicationFile] = useState(null); // Store OCR upload info for driver file creation
  // Driver's License AI parse state — mirrors Units' "Fill from Registration (AI)" flow.
  const [licenseParsing, setLicenseParsing] = useState(false);
  const [uploadedLicenseFile, setUploadedLicenseFile] = useState(null); // {file_id, filename}

  // Driver details dialog state
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedDriverDetails, setSelectedDriverDetails] = useState(null);

  const openDriverDetails = (driver) => {
    setSelectedDriverDetails(driver);
    setDetailsDialogOpen(true);
  };

  // Get current week start (Monday)
  const getCurrentWeekStart = useCallback(() => {
    return getWeekStart(new Date());
  }, []);

  // Get current week end (Sunday)
  const getCurrentWeekEnd = useCallback(() => {
    return addDaysYMD(getCurrentWeekStart(), 6);
  }, [getCurrentWeekStart]);

  useEffect(() => {
    loadData();
  }, []);

  // Load pay summaries after drivers are loaded
  useEffect(() => {
    if (drivers.length > 0) {
      loadPaySummaries();
    }
  }, [drivers]);

  const loadPaySummaries = async () => {
    if (!drivers.some(d => d.assigned_unit_id)) return;
    setLoadingSummaries(true);
    try {
      const res = await driversApi.getPaySummaries(getCurrentWeekStart());
      setPaySummaries(res.data || {});
    } catch (e) {
      // Non-critical — the list still renders without weekly figures
    } finally {
      setLoadingSummaries(false);
    }
  };

  const handleSetDriverStatus = async (driver, status) => {
    if (status !== 'active' && !window.confirm(`Mark ${driver.name} inactive? Their history and past paystubs stay intact.`)) return;
    try {
      await driversApi.setStatus(driver.id, status);
      setDrivers((prev) => prev.map((d) => (d.id === driver.id ? { ...d, status } : d)));
      toast.success(`${driver.name} ${status === 'active' ? 'reactivated' : 'marked inactive'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update status');
    }
  };

  const loadData = async () => {
    try {
      const [driversRes, unitsRes] = await Promise.all([
        driversApi.getAll(),
        unitsApi.getAll()
      ]);
      setDrivers(driversRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Combine address fields for legacy support
      const fullAddress = [
        formData.street_address,
        formData.city,
        formData.state,
        formData.zipcode
      ].filter(Boolean).join(', ');
      
      const data = {
        ...formData,
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        address: fullAddress || null,
        street_address: formData.street_address || null,
        city: formData.city || null,
        state: formData.state || null,
        zipcode: formData.zipcode || null,
        license_number: formData.license_number || null,
        license_expiry: formData.license_expiry || null,
        assigned_unit_id: formData.assigned_unit_id === 'none' ? null : formData.assigned_unit_id,
        unit_start_date: formData.unit_start_date || null,
        is_owner_operator: formData.is_owner_operator,
        ssn_last_four: formData.ssn_last_four || null,
        pay_type: formData.pay_type,
        pay_rate: formData.pay_rate ? parseFloat(formData.pay_rate) : null,
        auto_deduct_from_unit: formData.auto_deduct_from_unit,
        show_load_amounts_on_paystub: !!formData.show_load_amounts_on_paystub,
        block_pay: formData.block_pay !== '' && formData.block_pay != null ? parseFloat(formData.block_pay) : null,
      };

      let newDriverId = null;
      if (editingDriver) {
        await driversApi.update(editingDriver.id, data);
        toast.success('Driver updated successfully');
      } else {
        const res = await driversApi.create(data);
        newDriverId = res.data.id;
        toast.success('Driver created successfully');
        
        // If we have an uploaded application file from OCR, create a driver file record
        if (uploadedApplicationFile && newDriverId) {
          try {
            await driverFilesApi.create({
              driver_id: newDriverId,
              file_type: 'application',
              title: 'Driver Application',
              description: `Application extracted via AI from ${uploadedApplicationFile.file_name}`,
              expiration_date: '',
              file_id: uploadedApplicationFile.file_id,
              file_name: uploadedApplicationFile.file_name,
            });
            toast.success('Driver application saved to files!');
          } catch (fileError) {
            console.error('Failed to save driver file:', fileError);
          }
        }

        // If we have a driver's license file from the new AI parse flow, create a driver file record too
        if (uploadedLicenseFile && newDriverId) {
          try {
            await driverFilesApi.create({
              driver_id: newDriverId,
              file_type: 'license',
              title: "Driver's License",
              description: `License extracted via AI from ${uploadedLicenseFile.filename}`,
              expiration_date: data.license_expiry || '',
              file_id: uploadedLicenseFile.file_id,
              file_name: uploadedLicenseFile.filename,
            });
            toast.success("Driver's license saved to files!");
          } catch (fileError) {
            console.error('Failed to save license file:', fileError);
          }
        }
      }
      setDialogOpen(false);
      resetForm();
      setUploadedApplicationFile(null); // Clear the uploaded file info
      setUploadedLicenseFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  // Deep link: /drivers?edit=<id> opens that driver's edit dialog (used from Summary → Driver Paystubs)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const id = searchParams.get('edit');
    if (!id || drivers.length === 0) return;
    const d = drivers.find((x) => x.id === id);
    if (d) handleEdit(d);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers, searchParams]);

  const handleEdit = (driver) => {
    setEditingDriver(driver);
    setFormData({
      name: driver.name,
      email: driver.email || '',
      phone: driver.phone || '',
      street_address: driver.street_address || '',
      city: driver.city || '',
      state: driver.state || '',
      zipcode: driver.zipcode || '',
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      status: driver.status,
      assigned_unit_id: driver.assigned_unit_id || 'none',
      unit_start_date: driver.unit_start_date || '',
      is_owner_operator: driver.is_owner_operator || false,
      ssn_last_four: driver.ssn_last_four || '',
      pay_type: driver.pay_type || 'percentage',
      pay_rate: driver.pay_rate?.toString() || '',
      auto_deduct_from_unit: driver.auto_deduct_from_unit || false,
      show_load_amounts_on_paystub: driver.show_load_amounts_on_paystub || false,
      block_pay: driver.block_pay?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this driver?')) return;
    try {
      await driversApi.delete(id);
      toast.success('Driver deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  // Open paystub dialog
  const openPaystubDialog = (driver) => {
    setSelectedDriverForPaystub(driver);
    setPaystubStartDate(getCurrentWeekStart());
    setPaystubEndDate('');
    setPaystubDialogOpen(true);
  };

  // Open deductions dialog
  const openDeductionsDialog = async (driver) => {
    setSelectedDriverForDeductions(driver);
    setDeductionFormData({
      name: '',
      amount: '',
      deduction_type: 'advance',
      week_start: getCurrentWeekStart(),
      notes: ''
    });
    setDeductionsDialogOpen(true);
    await loadDriverDeductions(driver.id);
  };

  // Load deductions for a driver
  const loadDriverDeductions = async (driverId) => {
    setLoadingDeductions(true);
    try {
      const res = await driverDeductionsApi.getAll(driverId);
      setDriverDeductions(res.data || []);
    } catch (error) {
      toast.error('Failed to load deductions');
    } finally {
      setLoadingDeductions(false);
    }
  };

  // Add new deduction
  const handleAddDeduction = async (e) => {
    e.preventDefault();
    if (!selectedDriverForDeductions) return;

    try {
      await driverDeductionsApi.create({
        driver_id: selectedDriverForDeductions.id,
        name: deductionFormData.name,
        amount: parseFloat(deductionFormData.amount),
        deduction_type: deductionFormData.deduction_type,
        week_start: deductionFormData.week_start,
        notes: deductionFormData.notes || null
      });
      toast.success('Deduction added');
      setDeductionFormData({
        name: '',
        amount: '',
        deduction_type: 'advance',
        week_start: getCurrentWeekStart(),
        notes: ''
      });
      await loadDriverDeductions(selectedDriverForDeductions.id);
      loadPaySummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add deduction');
    }
  };

  // Delete a deduction
  const handleDeleteDeduction = async (deductionId) => {
    if (!window.confirm('Are you sure you want to delete this deduction?')) return;
    try {
      await driverDeductionsApi.delete(deductionId);
      toast.success('Deduction deleted');
      await loadDriverDeductions(selectedDriverForDeductions.id);
      loadPaySummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete deduction');
    }
  };

  // Set "This Week" preset for paystub
  const setThisWeekPreset = () => {
    setPaystubStartDate(getCurrentWeekStart());
    setPaystubEndDate(getCurrentWeekEnd());
  };

  // Open addons dialog
  const openAddonsDialog = async (driver) => {
    setSelectedDriverForAddons(driver);
    setAddonFormData({
      name: '',
      amount: '',
      addon_type: 'bonus',
      week_start: getCurrentWeekStart(),
      notes: ''
    });
    setAddonsDialogOpen(true);
    await loadDriverAddons(driver.id);
  };

  // Load addons for a driver
  const loadDriverAddons = async (driverId) => {
    setLoadingAddons(true);
    try {
      const res = await driverAddonsApi.getAll(driverId);
      setDriverAddons(res.data || []);
    } catch (error) {
      toast.error('Failed to load addons');
    } finally {
      setLoadingAddons(false);
    }
  };

  // Add new addon (bonus, etc.)
  const handleAddAddon = async (e) => {
    e.preventDefault();
    if (!selectedDriverForAddons) return;

    try {
      await driverAddonsApi.create({
        driver_id: selectedDriverForAddons.id,
        name: addonFormData.name,
        amount: parseFloat(addonFormData.amount),
        addon_type: addonFormData.addon_type,
        week_start: addonFormData.week_start,
        notes: addonFormData.notes || null
      });
      toast.success('Bonus/addon added');
      setAddonFormData({
        name: '',
        amount: '',
        addon_type: 'bonus',
        week_start: getCurrentWeekStart(),
        notes: ''
      });
      await loadDriverAddons(selectedDriverForAddons.id);
      loadPaySummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add bonus');
    }
  };

  // Delete an addon
  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this bonus/addon?')) return;
    try {
      await driverAddonsApi.delete(addonId);
      toast.success('Bonus/addon deleted');
      await loadDriverAddons(selectedDriverForAddons.id);
      loadPaySummaries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete bonus');
    }
  };

  // Handle OCR file upload
  const handleOCRUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setExtractingInfo(true);
    try {
      const res = await aiApi.extractDriverInfo(file);
      const extracted = res.data.extracted_data;
      
      if (extracted.error) {
        toast.error(extracted.error);
        return;
      }

      // Store the uploaded file info for creating driver file later
      setUploadedApplicationFile({
        file_id: res.data.file_id,
        file_name: res.data.file_name,
        file_size: res.data.file_size
      });

      // Pre-fill form with extracted data
      setFormData(prev => ({
        ...prev,
        name: extracted.name || prev.name,
        email: extracted.email || prev.email,
        phone: extracted.phone || prev.phone,
        street_address: extracted.street_address || prev.street_address,
        city: extracted.city || prev.city,
        state: extracted.state || prev.state,
        zipcode: extracted.zipcode || prev.zipcode,
        license_number: extracted.license_number || prev.license_number,
        license_expiry: extracted.license_expiry || prev.license_expiry,
        ssn_last_four: extracted.ssn_last_four || prev.ssn_last_four,
      }));

      toast.success('Driver information extracted! Please review and save.');
      setOcrDialogOpen(false);
      setDialogOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to extract driver information');
    } finally {
      setExtractingInfo(false);
    }
  };

  // Driver's License AI parse — simpler path than the full application OCR.
  // Uses /api/ai/parse-document?doc_type=driver_license (Gemini 3 Flash vision).
  const handleAiParseLicense = async (file) => {
    if (!file) return;
    setLicenseParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'driver_license');
      const res = await api.post('/ai/parse-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const parsed = res.data?.parsed || {};
      setUploadedLicenseFile({
        file_id: res.data?.file_id,
        filename: res.data?.filename,
      });
      // Build a name from first+last ONLY (NO middle name)
      const fullName = [parsed.first_name, parsed.last_name].filter(Boolean).join(' ').trim();
      setFormData((prev) => ({
        ...prev,
        name: fullName || prev.name,
        license_number: parsed.license_number || prev.license_number,
        license_expiry: parsed.license_expiry || prev.license_expiry,
        state: parsed.state || prev.state,
        street_address: parsed.street_address || prev.street_address,
        city: parsed.city || prev.city,
        zipcode: parsed.zip_code || prev.zipcode,
      }));
      if (parsed.license_number || fullName) {
        toast.success("Driver's license extracted — review and save");
      } else {
        toast.info('File uploaded, but no fields were extracted. Please fill them in.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extract failed');
    } finally {
      setLicenseParsing(false);
    }
  };

  // Preview state for paystub PDF
  const [paystubPreviewUrl, setPaystubPreviewUrl] = useState(null);

  // Generate paystub — preview inline (no auto-download).
  // The dialog shows the PDF in an iframe; user can click "Download" to save.
  const handleGeneratePaystub = async () => {
    if (!selectedDriverForPaystub || !paystubStartDate) return;
    
    if (!selectedDriverForPaystub.assigned_unit_id) {
      toast.error('Driver must be assigned to a unit to generate paystub');
      return;
    }
    
    setGeneratingPaystub(true);
    try {
      const endDate = paystubEndDate && paystubEndDate !== paystubStartDate ? paystubEndDate : null;
      const response = await driversApi.getPaystub(selectedDriverForPaystub.id, paystubStartDate, endDate);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (paystubPreviewUrl) window.URL.revokeObjectURL(paystubPreviewUrl);
      const url = window.URL.createObjectURL(blob);
      setPaystubPreviewUrl(url);
      toast.success('Paystub generated — preview ready');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate paystub');
    } finally {
      setGeneratingPaystub(false);
    }
  };

  // Explicit download button — only triggers a save after preview
  const handleDownloadPaystubPdf = () => {
    if (!paystubPreviewUrl || !selectedDriverForPaystub) return;
    const endDate = paystubEndDate && paystubEndDate !== paystubStartDate ? paystubEndDate : null;
    const filename = endDate
      ? `Paystub_${selectedDriverForPaystub.name.replace(/\s+/g, '_')}_${paystubStartDate}_to_${endDate}.pdf`
      : `Paystub_${selectedDriverForPaystub.name.replace(/\s+/g, '_')}_${paystubStartDate}.pdf`;
    const link = document.createElement('a');
    link.href = paystubPreviewUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetForm = () => {
    setEditingDriver(null);
    setUploadedApplicationFile(null); // Clear uploaded file info
    setFormData({
      name: '',
      email: '',
      phone: '',
      street_address: '',
      city: '',
      state: '',
      zipcode: '',
      license_number: '',
      license_expiry: '',
      status: 'active',
      assigned_unit_id: 'none',
      unit_start_date: '',
      is_owner_operator: false,
      ssn_last_four: '',
      pay_type: 'percentage',
      pay_rate: '',
      auto_deduct_from_unit: false,
      show_load_amounts_on_paystub: false,
      block_pay: '',
    });
  };

  const filteredDrivers = drivers.filter(driver => {
    const isInactive = driver.status === 'inactive';
    if (statusFilter === 'active' && isInactive) return false;
    if (statusFilter === 'inactive' && !isInactive) return false;
    return driver.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phone?.includes(searchTerm);
  });
  const inactiveDriverCount = drivers.filter(d => d.status === 'inactive').length;

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    inactive: 'bg-slate-100 text-slate-700',
    on_leave: 'bg-amber-100 text-amber-700',
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="drivers-loading">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-64 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="drivers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-manrope font-bold text-foreground">Drivers</h1>
          <p className="text-muted-foreground mt-1">Manage your fleet drivers</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {/* OCR Upload Button */}
            <Dialog open={ocrDialogOpen} onOpenChange={setOcrDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-blue-200 text-blue-600 hover:bg-blue-50" data-testid="ocr-upload-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  AI Upload
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                  <DialogTitle className="font-manrope flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-blue-600" />
                    AI Driver Upload
                  </DialogTitle>
                  <DialogDescription>
                    Upload a driver application and AI will extract the information automatically.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {extractingInfo ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        <p className="text-sm text-muted-foreground">Analyzing document...</p>
                      </div>
                    </div>
                  ) : (
                    <DragDropZone
                      onFileSelect={(file) => handleOCRUpload({ target: { files: [file] } })}
                      accept=".pdf"
                      data-testid="ocr-file-input"
                    >
                      <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" />
                      <p className="text-sm font-medium">Drag & drop or click to upload driver application</p>
                      <p className="text-xs text-muted-foreground mt-1">PDF files only, up to 10MB</p>
                    </DragDropZone>
                  )}
                  <p className="text-xs text-muted-foreground">
                    AI will extract: Name, Email, Phone, Address, License Info, SSN (last 4)
                  </p>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Driver Button */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-driver-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Driver
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle className="font-manrope">
                  {editingDriver ? 'Edit Driver' : 'Add New Driver'}
                </DialogTitle>
                <DialogDescription>
                  {editingDriver ? 'Update driver information' : 'Add a new driver to your team'}
                </DialogDescription>
              </DialogHeader>
              {!editingDriver && (
                <div
                  className="rounded-lg border-2 border-dashed border-blue-300 dark:border-blue-700 bg-blue-50/70 dark:bg-blue-950/30 p-3 space-y-2 transition-colors"
                  onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-100'); }}
                  onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100'); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-100');
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleAiParseLicense(f);
                  }}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-blue-900 dark:text-blue-100">
                      <Sparkles className="h-4 w-4 text-blue-600" />
                      Extract from Driver's License or Application
                    </div>
                    {uploadedLicenseFile && (
                      <span className="text-[11px] text-blue-700 dark:text-blue-300 truncate max-w-[180px]" title={uploadedLicenseFile.filename}>
                        {uploadedLicenseFile.filename}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-blue-800/80 dark:text-blue-200/80">
                    Drag &amp; drop or click to upload a photo/PDF of the driver's license, CDL, or application.
                    Name, license #, expiration, state, and address are extracted automatically.
                    The file is saved under this driver's Driver Files.
                  </p>
                  <label className="inline-flex">
                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp,application/pdf"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleAiParseLicense(f);
                        e.target.value = '';
                      }}
                      data-testid="ai-parse-license-input"
                    />
                    <span
                      className={`inline-flex items-center gap-2 text-xs font-medium rounded-md border border-blue-300 dark:border-blue-700 bg-white dark:bg-slate-900 px-3 py-1.5 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 ${licenseParsing ? 'opacity-60 pointer-events-none' : ''}`}
                      data-testid="ai-parse-license-btn"
                    >
                      {licenseParsing ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Upload className="h-3.5 w-3.5" />
                      )}
                      {licenseParsing ? 'Extracting…' : 'Upload or Drop File'}
                    </span>
                  </label>
                </div>
              )}
                <form onSubmit={handleSubmit} className="space-y-4 pb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      required
                      data-testid="driver-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="driver-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="on_leave">On Leave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      data-testid="driver-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      data-testid="driver-phone-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="street_address">Street Address</Label>
                  <Input
                    id="street_address"
                    value={formData.street_address}
                    onChange={(e) => setFormData({ ...formData, street_address: e.target.value })}
                    placeholder="123 Main St"
                    data-testid="driver-street-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="Dallas"
                      data-testid="driver-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      placeholder="TX"
                      data-testid="driver-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zipcode">ZIP Code</Label>
                    <Input
                      id="zipcode"
                      value={formData.zipcode}
                      onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                      placeholder="75001"
                      data-testid="driver-zipcode-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="license_number">License Number</Label>
                    <Input
                      id="license_number"
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      placeholder="CDL Number"
                      data-testid="driver-license-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="license_expiry">License Expiry</Label>
                    <Input
                      id="license_expiry"
                      type="date"
                      value={formData.license_expiry}
                      onChange={(e) => setFormData({ ...formData, license_expiry: e.target.value })}
                      data-testid="driver-expiry-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit">Assign to Unit</Label>
                  <Select
                    value={formData.assigned_unit_id}
                    onValueChange={(value) => setFormData({ ...formData, assigned_unit_id: value })}
                  >
                    <SelectTrigger data-testid="driver-unit-select">
                      <SelectValue placeholder="Select unit (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Truck className="h-4 w-4" />
                          No unit assigned
                        </div>
                      </SelectItem>
                      {units.filter(u => u.status === 'active').map((unit) => (
                        <SelectItem key={unit.id} value={unit.id}>
                          <div className="flex items-center gap-2">
                            <Truck className="h-4 w-4 text-blue-600" />
                            <span className="font-medium">{unit.unit_number}</span>
                            <span className="text-muted-foreground text-xs">
                              {unit.make} {unit.model}
                            </span>
                            {unit.assigned_driver_name && unit.assigned_driver_id !== editingDriver?.id && (
                              <Badge variant="outline" className="text-xs text-amber-600">
                                Assigned
                              </Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {formData.assigned_unit_id && formData.assigned_unit_id !== 'none' && (
                    <div className="space-y-2 mt-2">
                      <Label htmlFor="unit_start_date">Unit Start Date</Label>
                      <Input
                        id="unit_start_date"
                        type="date"
                        value={formData.unit_start_date}
                        onChange={(e) => setFormData({ ...formData, unit_start_date: e.target.value })}
                        data-testid="driver-unit-start-date"
                      />
                      <p className="text-xs text-muted-foreground">
                        Paystubs will only include loads from this date forward.
                      </p>
                    </div>
                  )}
                  {units.length === 0 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      No units created yet. Add units first.
                    </p>
                  )}
                </div>
                
                {/* Pay Settings Section */}
                <div className="border border-emerald-200 dark:border-emerald-800 rounded-lg p-4 bg-emerald-50/50 dark:bg-emerald-950/20">
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                    <Label className="font-semibold text-emerald-800 dark:text-emerald-300">Pay Settings</Label>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">SSN (Last 4)</Label>
                      <Input
                        value={formData.ssn_last_four}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                          setFormData({ ...formData, ssn_last_four: val });
                        }}
                        placeholder="1234"
                        maxLength={4}
                        data-testid="driver-ssn-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Pay Type</Label>
                      <Select
                        value={formData.pay_type}
                        onValueChange={(value) => setFormData({ ...formData, pay_type: value })}
                      >
                        <SelectTrigger data-testid="driver-pay-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Percentage of Gross</SelectItem>
                          <SelectItem value="percentage_net">Percentage of Net</SelectItem>
                          <SelectItem value="salary">Fixed Salary (weekly)</SelectItem>
                          <SelectItem value="hourly">Hourly Rate</SelectItem>
                          <SelectItem value="per_mile">Per Mile</SelectItem>
                          <SelectItem value="per_load_flat">Per Load (flat)</SelectItem>
                          <SelectItem value="flat_per_active_week">Flat / Active Week</SelectItem>
                          <SelectItem value="custom_amount">Custom Amount (per paystub)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">
                        {(() => {
                          const t = formData.pay_type;
                          if (t === 'percentage' || t === 'percentage_net') return 'Percentage (%)';
                          if (t === 'per_mile') return 'Rate per Mile ($)';
                          if (t === 'hourly') return 'Hourly Rate ($)';
                          if (t === 'per_load_flat') return 'Amount per Load ($)';
                          if (t === 'flat_per_active_week') return 'Flat Amount per Active Week ($)';
                          if (t === 'custom_amount') return 'Default Amount ($) — set per paystub';
                          return 'Weekly Salary ($)';
                        })()}
                      </Label>
                      <Input
                        type="number"
                        step={['percentage', 'percentage_net'].includes(formData.pay_type) ? '0.1' : '0.01'}
                        value={formData.pay_rate}
                        onChange={(e) => setFormData({ ...formData, pay_rate: e.target.value })}
                        placeholder={(() => {
                          const t = formData.pay_type;
                          if (t === 'percentage' || t === 'percentage_net') return '25';
                          if (t === 'per_mile') return '0.55';
                          if (t === 'hourly') return '22.50';
                          if (t === 'per_load_flat') return '50';
                          if (t === 'flat_per_active_week') return '1200';
                          if (t === 'custom_amount') return '0';
                          return '1500';
                        })()}
                        data-testid="driver-pay-rate-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {(() => {
                      const t = formData.pay_type;
                      if (t === 'percentage') return "Driver pay = percentage of the assigned unit's gross revenue for the week.";
                      if (t === 'percentage_net') return "Driver pay = percentage of unit NET (gross − dispatch fees − unit deductions).";
                      if (t === 'salary') return 'Fixed weekly salary — independent of loads.';
                      if (t === 'hourly') return 'Driver pay = rate × hours worked. Enter hours on each paystub.';
                      if (t === 'per_mile') return 'Driver pay = rate × total miles from loads.';
                      if (t === 'per_load_flat') return 'Driver pay = flat amount × number of loads delivered.';
                      if (t === 'flat_per_active_week') return 'Flat amount if the unit had at least 1 load that week, otherwise $0.';
                      return 'Fully manual — enter the exact dollar amount on each paystub.';
                    })()}
                  </p>
                  
                  {/* Auto-Deduct Checkbox */}
                  {formData.assigned_unit_id && formData.assigned_unit_id !== 'none' && (
                    <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 mt-3">
                      <Checkbox
                        id="auto_deduct_from_unit"
                        checked={formData.auto_deduct_from_unit}
                        onCheckedChange={(checked) => setFormData({ ...formData, auto_deduct_from_unit: checked })}
                        data-testid="driver-auto-deduct-checkbox"
                      />
                      <div className="flex flex-col">
                        <Label htmlFor="auto_deduct_from_unit" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-green-600" />
                          Auto-Deduct Pay from Unit
                        </Label>
                        <span className="text-xs text-muted-foreground">
                          Automatically add driver pay as a "Driver Pay" deduction on the assigned unit each week
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Paystub display + block pay */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="flex items-start space-x-3 p-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-950/30">
                      <Checkbox
                        id="show_load_amounts_on_paystub"
                        checked={!!formData.show_load_amounts_on_paystub}
                        onCheckedChange={(checked) => setFormData({ ...formData, show_load_amounts_on_paystub: checked })}
                        data-testid="driver-show-load-amounts-checkbox"
                      />
                      <div className="flex flex-col">
                        <Label htmlFor="show_load_amounts_on_paystub" className="text-sm font-medium cursor-pointer">Show load amounts on paystub</Label>
                        <span className="text-xs text-muted-foreground">Off: paystub lists load details only. On: also shows each load's broker gross, rate and driver pay, with totals.</span>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="block_pay" className="text-sm">Block pay ($ per block load)</Label>
                      <Input
                        id="block_pay"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g. 450"
                        value={formData.block_pay}
                        onChange={(e) => setFormData({ ...formData, block_pay: e.target.value })}
                        data-testid="driver-block-pay-input"
                      />
                      <span className="text-xs text-muted-foreground">Pre-fills loads whose reference starts with "B-" in Calculate pay.</span>
                    </div>
                  </div>
                </div>
                
                {/* Owner Operator Checkbox */}
                <div className="flex items-center space-x-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <Checkbox
                    id="is_owner_operator"
                    checked={formData.is_owner_operator}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_owner_operator: checked })}
                    data-testid="driver-owner-operator-checkbox"
                  />
                  <div className="flex flex-col">
                    <Label htmlFor="is_owner_operator" className="text-sm font-medium cursor-pointer flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-amber-600" />
                      Also an Owner Operator
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      If checked, this driver will also appear in the Owner Operators page
                    </span>
                  </div>
                </div>
                
                <DialogFooter className="pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="driver-submit-btn">
                    {editingDriver ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search drivers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
              data-testid="drivers-search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[190px]" data-testid="drivers-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="inactive">Inactive only ({inactiveDriverCount})</SelectItem>
              <SelectItem value="all">All drivers</SelectItem>
            </SelectContent>
          </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Assigned Unit</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDrivers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-muted-foreground">
                    {drivers.length === 0 ? (
                      <div className="flex flex-col items-center gap-2">
                        <Users className="h-8 w-8 text-muted-foreground/50" />
                        <p>No drivers yet. Add your first driver!</p>
                      </div>
                    ) : (
                      statusFilter === 'inactive' ? 'No inactive drivers' : 'No drivers match your search'
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredDrivers.map((driver) => (
                  <TableRow key={driver.id} data-testid={`driver-row-${driver.id}`}>
                    <TableCell>
                      <div className="flex flex-col">
                        <button 
                          onClick={() => openDriverDetails(driver)}
                          className="font-medium text-left text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          data-testid={`driver-name-${driver.id}`}
                        >
                          {driver.name}
                        </button>
                        {driver.assigned_unit_id && paySummaries[driver.id] && (
                          <div className="flex items-center gap-3 mt-1 text-xs">
                            <span className="flex items-center gap-1 text-emerald-600">
                              <TrendingUp className="h-3 w-3" />
                              Week: ${paySummaries[driver.id].net_pay?.toLocaleString() || '0'}
                            </span>
                            <span className="flex items-center gap-1 text-blue-600">
                              YTD: ${paySummaries[driver.id].ytd?.net_pay?.toLocaleString() || '0'}
                            </span>
                          </div>
                        )}
                        {driver.assigned_unit_id && loadingSummaries && !paySummaries[driver.id] && (
                          <Skeleton className="h-3 w-32 mt-1" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1 text-sm">
                        {driver.email && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="h-3 w-3" />
                            {driver.email}
                          </span>
                        )}
                        {driver.phone && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3 w-3" />
                            {driver.phone}
                          </span>
                        )}
                        {!driver.email && !driver.phone && '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      {driver.license_number || '-'}
                    </TableCell>
                    <TableCell>{driver.assigned_unit_number || '-'}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[driver.status]}>
                        {driver.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    {canEdit && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2 flex-wrap">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openPaystubDialog(driver)}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`paystub-driver-${driver.id}`}
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Paystub
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(driver)}
                            data-testid={`edit-driver-${driver.id}`}
                          >
                            <Pencil className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          {driver.status === 'inactive' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSetDriverStatus(driver, 'active')}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`reactivate-driver-${driver.id}`}
                            >
                              <UserCheck className="h-4 w-4 mr-1" />
                              Reactivate
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetDriverStatus(driver, 'inactive')}
                              className="text-slate-500 hover:text-slate-700"
                              data-testid={`deactivate-driver-${driver.id}`}
                            >
                              Deactivate
                            </Button>
                          )}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(driver.id)}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-driver-${driver.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paystub Generation Dialog */}
      <Dialog
        open={paystubDialogOpen}
        onOpenChange={(o) => {
          setPaystubDialogOpen(o);
          if (!o) {
            if (paystubPreviewUrl) window.URL.revokeObjectURL(paystubPreviewUrl);
            setPaystubPreviewUrl(null);
          }
        }}
      >
        <DialogContent
          className={
            paystubPreviewUrl
              ? 'max-w-5xl w-[95vw] h-[92vh] sm:h-[88vh] flex flex-col p-0 sm:p-6 gap-0 sm:gap-4 overflow-hidden'
              : 'sm:max-w-[800px] max-h-[90vh] overflow-y-auto'
          }
        >
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <FileText className="h-5 w-5 text-emerald-600" />
              {paystubPreviewUrl ? 'Driver Paystub Preview' : 'Generate Driver Paystub'}
            </DialogTitle>
            {selectedDriverForPaystub && !paystubPreviewUrl && (
              <DialogDescription>
                Generate a pay stub for <strong>{selectedDriverForPaystub.name}</strong>
                {selectedDriverForPaystub.assigned_unit_number && (
                  <span className="block text-sm mt-1">
                    Assigned Unit: <strong>{selectedDriverForPaystub.assigned_unit_number}</strong>
                  </span>
                )}
                {selectedDriverForPaystub.pay_type && (
                  <span className="block text-sm mt-1">
                    Pay Type: <strong>
                      {(() => {
                        const t = selectedDriverForPaystub.pay_type;
                        const r = selectedDriverForPaystub.pay_rate || 0;
                        if (t === 'percentage' || t === 'percentage_gross') return `${r}% of Gross`;
                        if (t === 'percentage_net') return `${r}% of Net`;
                        if (t === 'per_mile') return `$${r} / mile`;
                        if (t === 'per_load_flat') return `$${r} / load`;
                        if (t === 'flat_per_active_week') return `$${r} flat (active weeks)`;
                        if (t === 'salary') return `$${r} weekly salary`;
                        if (t === 'hourly') return `$${r} / hour`;
                        if (t === 'custom_amount') return 'Custom amount per paystub';
                        return `$${r}`;
                      })()}
                    </strong>
                  </span>
                )}
              </DialogDescription>
            )}
          </DialogHeader>
          <div className={paystubPreviewUrl ? 'p-3 sm:p-0 sm:space-y-4 sm:py-4 overflow-y-auto flex-1' : 'space-y-4 py-4 min-w-0'}>
            {/* Date inputs — hidden by default once preview is shown; click "Change dates" to reopen */}
            {!paystubPreviewUrl && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={setThisWeekPreset}
                  className="text-xs"
                  data-testid="paystub-this-week-btn"
                >
                  <Calendar className="h-3 w-3 mr-1" />
                  This Week
                </Button>
              </div>
            )}
            
            {!paystubPreviewUrl && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="paystub-start">Start Date</Label>
                <Input
                  id="paystub-start"
                  type="date"
                  value={paystubStartDate}
                  onChange={(e) => setPaystubStartDate(e.target.value)}
                  data-testid="paystub-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="paystub-end">End Date (Optional)</Label>
                <Input
                  id="paystub-end"
                  type="date"
                  value={paystubEndDate}
                  onChange={(e) => setPaystubEndDate(e.target.value)}
                  data-testid="paystub-end-date"
                />
              </div>
            </div>
            )}
            {!paystubPreviewUrl && (
            <p className="text-xs text-muted-foreground">
              Leave end date empty to generate paystub for a single week. Set both dates for a date range.
            </p>
            )}

            {selectedDriverForPaystub?.assigned_unit_id && !paystubPreviewUrl && paystubStartDate && (
              <DriverPaystubLive
                driverId={selectedDriverForPaystub.id}
                startDate={paystubStartDate}
                endDate={paystubEndDate}
              />
            )}
            
            {selectedDriverForPaystub && !selectedDriverForPaystub.assigned_unit_id && !paystubPreviewUrl && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-700 dark:text-amber-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Driver must be assigned to a unit to generate paystub.</span>
              </div>
            )}

            {/* Inline PDF preview — iframe on desktop, Open-in-tab button on mobile */}
            {paystubPreviewUrl && (
              <div className="border rounded-md overflow-hidden bg-slate-100 flex-1 flex flex-col min-h-0">
                <iframe
                  src={paystubPreviewUrl}
                  title="Driver paystub preview"
                  className="hidden sm:block w-full flex-1 min-h-[60vh]"
                  data-testid="driver-paystub-preview-iframe"
                />
                <div className="sm:hidden flex flex-col items-center gap-3 py-8 px-4 text-center">
                  <FileText className="h-12 w-12 text-emerald-600" />
                  <p className="text-sm text-slate-700">Paystub PDF generated. Tap to open the full preview in a new tab.</p>
                  <Button
                    onClick={() => window.open(paystubPreviewUrl, '_blank')}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    data-testid="driver-paystub-preview-open-mobile"
                  >
                    <FileText className="h-4 w-4 mr-2" />Open Paystub PDF
                  </Button>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="p-3 sm:p-0 border-t sm:border-t-0 flex-row flex-wrap gap-2 justify-end">
            <Button variant="outline" onClick={() => setPaystubDialogOpen(false)} size="sm">
              Close
            </Button>
            {paystubPreviewUrl && (
              <Button
                onClick={() => {
                  if (paystubPreviewUrl) window.URL.revokeObjectURL(paystubPreviewUrl);
                  setPaystubPreviewUrl(null);
                }}
                variant="outline"
                size="sm"
                data-testid="change-paystub-dates"
              >
                <Calendar className="h-4 w-4 mr-1" />
                Change Dates
              </Button>
            )}
            {paystubPreviewUrl && (
              <Button
                onClick={handleDownloadPaystubPdf}
                variant="outline"
                size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="download-paystub-pdf"
              >
                <Download className="h-4 w-4 mr-1" />
                Download PDF
              </Button>
            )}
            <Button
              onClick={handleGeneratePaystub}
              disabled={!selectedDriverForPaystub?.assigned_unit_id || generatingPaystub}
              className="bg-emerald-600 hover:bg-emerald-700"
              size="sm"
              data-testid="generate-paystub-btn"
            >
              {generatingPaystub ? 'Generating...' : (paystubPreviewUrl ? 'Refresh' : 'Generate Preview')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver Deductions Management Dialog */}
      <Dialog open={deductionsDialogOpen} onOpenChange={setDeductionsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Minus className="h-5 w-5 text-amber-600" />
              Manage Deductions
            </DialogTitle>
            {selectedDriverForDeductions && (
              <DialogDescription>
                Manage deductions for <strong>{selectedDriverForDeductions.name}</strong>
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Add Deduction Form */}
          <form onSubmit={handleAddDeduction} className="space-y-4 border rounded-lg p-4 bg-slate-50 dark:bg-slate-900">
            <h4 className="font-medium flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" />
              Add New Deduction
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input
                  value={deductionFormData.name}
                  onChange={(e) => setDeductionFormData({ ...deductionFormData, name: e.target.value })}
                  placeholder="Cash Advance"
                  required
                  data-testid="deduction-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={deductionFormData.amount}
                  onChange={(e) => setDeductionFormData({ ...deductionFormData, amount: e.target.value })}
                  placeholder="100.00"
                  required
                  data-testid="deduction-amount-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select
                  value={deductionFormData.deduction_type}
                  onValueChange={(value) => setDeductionFormData({ ...deductionFormData, deduction_type: value })}
                >
                  <SelectTrigger data-testid="deduction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">Cash Advance</SelectItem>
                    <SelectItem value="fuel">Fuel</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Week Start *</Label>
                <Input
                  type="date"
                  value={deductionFormData.week_start}
                  onChange={(e) => setDeductionFormData({ ...deductionFormData, week_start: e.target.value })}
                  required
                  data-testid="deduction-week-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes (Optional)</Label>
              <Input
                value={deductionFormData.notes}
                onChange={(e) => setDeductionFormData({ ...deductionFormData, notes: e.target.value })}
                placeholder="Add any notes..."
                data-testid="deduction-notes-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="add-deduction-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Deduction
            </Button>
          </form>

          {/* Deductions List */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Existing Deductions</h4>
            {loadingDeductions ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : driverDeductions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No deductions found for this driver.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Week</TableHead>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {driverDeductions
                      .sort((a, b) => new Date(b.week_start) - new Date(a.week_start))
                      .map((ded) => (
                        <TableRow key={ded.id} data-testid={`deduction-row-${ded.id}`}>
                          <TableCell className="text-xs">{ded.week_start}</TableCell>
                          <TableCell className="text-xs font-medium">{ded.name}</TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-xs">
                              {ded.deduction_type || 'other'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-right text-red-600 font-medium">
                            -${ded.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteDeduction(ded.id)}
                              className="h-6 w-6 text-red-600 hover:text-red-700"
                              data-testid={`delete-deduction-${ded.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeductionsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver Add-ons/Bonuses Management Dialog */}
      <Dialog open={addonsDialogOpen} onOpenChange={setAddonsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Gift className="h-5 w-5 text-green-600" />
              Manage Bonuses & Add-ons
            </DialogTitle>
            {selectedDriverForAddons && (
              <DialogDescription>
                Manage bonuses and add-ons for <strong>{selectedDriverForAddons.name}</strong>
              </DialogDescription>
            )}
          </DialogHeader>

          {/* Add Addon Form */}
          <form onSubmit={handleAddAddon} className="space-y-4 border rounded-lg p-4 bg-green-50 dark:bg-green-900/20">
            <h4 className="font-medium flex items-center gap-2 text-sm">
              <Plus className="h-4 w-4" />
              Add New Bonus / Add-on
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input
                  value={addonFormData.name}
                  onChange={(e) => setAddonFormData({ ...addonFormData, name: e.target.value })}
                  placeholder="Safety Bonus"
                  required
                  data-testid="addon-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Amount ($) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={addonFormData.amount}
                  onChange={(e) => setAddonFormData({ ...addonFormData, amount: e.target.value })}
                  placeholder="100.00"
                  required
                  data-testid="addon-amount-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Type</Label>
                <Select
                  value={addonFormData.addon_type}
                  onValueChange={(value) => setAddonFormData({ ...addonFormData, addon_type: value })}
                >
                  <SelectTrigger data-testid="addon-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bonus">Bonus</SelectItem>
                    <SelectItem value="reimbursement">Reimbursement</SelectItem>
                    <SelectItem value="award">Safety Award</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Week Start *</Label>
                <Input
                  type="date"
                  value={addonFormData.week_start}
                  onChange={(e) => setAddonFormData({ ...addonFormData, week_start: e.target.value })}
                  required
                  data-testid="addon-week-input"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes (Optional)</Label>
              <Input
                value={addonFormData.notes}
                onChange={(e) => setAddonFormData({ ...addonFormData, notes: e.target.value })}
                placeholder="Add any notes..."
                data-testid="addon-notes-input"
              />
            </div>
            <Button type="submit" size="sm" className="bg-green-600 hover:bg-green-700" data-testid="add-addon-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Bonus/Add-on
            </Button>
          </form>

          {/* Addons List */}
          <div className="space-y-2">
            <h4 className="font-medium text-sm">Existing Bonuses & Add-ons</h4>
            {loadingAddons ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : driverAddons.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No bonuses or add-ons found for this driver.
              </p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">Week</TableHead>
                      <TableHead className="text-xs">Name</TableHead>
                      <TableHead className="text-xs">Type</TableHead>
                      <TableHead className="text-xs text-right">Amount</TableHead>
                      <TableHead className="text-xs text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {driverAddons
                      .sort((a, b) => new Date(b.week_start) - new Date(a.week_start))
                      .map((addon) => (
                        <TableRow key={addon.id} data-testid={`addon-row-${addon.id}`}>
                          <TableCell className="text-xs">{addon.week_start}</TableCell>
                          <TableCell className="text-xs font-medium">{addon.name}</TableCell>
                          <TableCell className="text-xs">
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              {addon.addon_type || 'bonus'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-right text-green-600 font-medium">
                            +${addon.amount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteAddon(addon.id)}
                              className="h-6 w-6 text-red-600 hover:text-red-700"
                              data-testid={`delete-addon-${addon.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddonsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Driver Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-manrope flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Driver Details
            </DialogTitle>
            {selectedDriverDetails && (
              <DialogDescription>
                Full information for <strong>{selectedDriverDetails.name}</strong>
              </DialogDescription>
            )}
          </DialogHeader>
          
          {selectedDriverDetails && (
            <div className="space-y-6 pb-4">
                {/* Basic Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Full Name</p>
                      <p className="font-medium">{selectedDriverDetails.name}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Status</p>
                      <Badge className={statusColors[selectedDriverDetails.status]}>
                        {selectedDriverDetails.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="font-medium">{selectedDriverDetails.email || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{selectedDriverDetails.phone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Address</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">Street Address</p>
                      <p className="font-medium">{selectedDriverDetails.street_address || selectedDriverDetails.address || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">City</p>
                      <p className="font-medium">{selectedDriverDetails.city || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">State</p>
                      <p className="font-medium">{selectedDriverDetails.state || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">ZIP Code</p>
                      <p className="font-medium">{selectedDriverDetails.zipcode || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* License Info */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">License Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">License Number</p>
                      <p className="font-medium">{selectedDriverDetails.license_number || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Expiry Date</p>
                      <p className="font-medium">{selectedDriverDetails.license_expiry || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Assignment & Pay */}
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Assignment & Pay</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Assigned Unit</p>
                      <p className="font-medium">{selectedDriverDetails.assigned_unit_number || 'Not Assigned'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Unit Start Date</p>
                      <p className="font-medium">{selectedDriverDetails.unit_start_date || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pay Type</p>
                      <p className="font-medium capitalize">{selectedDriverDetails.pay_type?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pay Rate</p>
                      <p className="font-medium">
                        {selectedDriverDetails.pay_rate 
                          ? selectedDriverDetails.pay_type === 'percentage' 
                            ? `${selectedDriverDetails.pay_rate}%` 
                            : selectedDriverDetails.pay_type === 'per_mile'
                            ? `$${selectedDriverDetails.pay_rate}/mile`
                            : `$${selectedDriverDetails.pay_rate}`
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Auto-Deduct from Unit</p>
                      <p className="font-medium">{selectedDriverDetails.auto_deduct_from_unit ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Owner Operator</p>
                      <p className="font-medium">{selectedDriverDetails.is_owner_operator ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                </div>

                {/* Sensitive Info */}
                <div className="space-y-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
                  <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-200 uppercase tracking-wide">Sensitive Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">SSN (Last 4)</p>
                      <p className="font-medium font-mono">{selectedDriverDetails.ssn_last_four ? `***-**-${selectedDriverDetails.ssn_last_four}` : '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Pay Summary (if available) */}
                {paySummaries[selectedDriverDetails.id] && (
                  <div className="space-y-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
                    <h4 className="text-sm font-semibold text-green-800 dark:text-green-200 uppercase tracking-wide">Pay Summary</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">This Week Net</p>
                        <p className="font-medium text-green-600 font-mono">
                          ${paySummaries[selectedDriverDetails.id].net_pay?.toLocaleString() || '0'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">YTD Net</p>
                        <p className="font-medium text-blue-600 font-mono">
                          ${paySummaries[selectedDriverDetails.id].ytd?.net_pay?.toLocaleString() || '0'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
          )}

          <DialogFooter className="pt-4 border-t">
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
            {canEdit && selectedDriverDetails && (
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  setDetailsDialogOpen(false);
                  handleEdit(selectedDriverDetails);
                }}
                data-testid="driver-details-edit-btn"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit Driver
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
